/**
 * Catch-all page for the pages that Contentful defines rather than a file in `src/pages/`. Next.js
 * routes a file in `src/pages/` ahead of this catch-all; `getStaticPaths` doesn't check its paths
 * against those files.
 *
 * At build it exports three kinds of entry, all from Contentful through `Crawl`
 * (`@ryerson/frontend.contentful`):
 * - **`page`** entries whose `website` list includes this build's company
 *   (`components/Shared/model/Contentful.tsx` → `companyToCompany`: `"Ryerson"` or
 *   `"Southern Tool Steel"`), at the entry's `path`.
 *   Rendered by `components/Contentful/ContentfulPage.tsx`.
 * - **`productCategoryPage`** entries at `/<slug>`, rendered by
 *   `components/Contentful/Product.tsx`.
 * - **`productSubCategoryPage`** entries at `/<baseSlug>/<slug>`, one query per category, rendered
 *   by `components/Contentful/SubProduct.tsx`.
 * When a product path is also a `page` path, the `page` wins: the product path isn't added, and
 * `getStaticProps` looks for a `page` first. The product queries are not filtered by company, so
 * both brand builds export every product entry.
 *
 * The product pages were `pages/[product]/index.tsx` and `pages/[product]/[subProduct].tsx` until
 * this catch-all replaced them. Their URLs are kept out of the sitemap by
 * `marketing-custom-paths.js`.
 *
 * Rules a change must keep:
 * - `fallback: false`: only the listed paths exist. A new Contentful entry appears on the site at
 *   the next build, not before.
 * - A `page` entry's `path` is looked up twice: split into segments here, then rebuilt as
 *   `"/" + segments.join("/")` and matched exactly against `fields.path` in `getStaticProps`
 *   (`ContentfulUtils.ts` → `getPageContent`). A `path` stored any other way, for example with a
 *   trailing slash, is exported but its content isn't found, so the page renders only the header
 *   and footer. The exception is when a `productCategoryPage` (one segment) or
 *   `productSubCategoryPage` (two segments) has the same segments: that product page renders at
 *   the URL instead.
 * - A Contentful failure fails the build (web-architecture ADR-0033). `Crawl` tries each request
 *   three times, 6 s apart, then throws, and nothing here catches it.
 * - `Crawl` reads its space, environment and token from `CONTENTFUL_SPACE_ID`,
 *   `CONTENTFUL_ENVIRONMENT` and `CONTENTFUL_ACCESS_TOKEN` (not the `NEXT_PUBLIC_` names) when no
 *   options are passed, as here.
 *
 * Contentful pages are not in the site-search index (`scripts/staticSearch.ts`). A draft `page` can
 * be viewed before publishing at `/preview/<path>` in the dev build
 * (`components/Contentful/ContentfulPagePreview.tsx`).
 */
import React from "react";
import { allContentfulHeaderCalls, HeaderPageProps } from "@ryerson/frontend.contentful";
import HeaderLayout from "@components/NextLayouts/HeaderLayout";
import { getHeaderData } from "@components/NextLayouts/nextUtilities";
import { LanguageContent } from "@components/Shared/model/LanguageContent";
import { Crawl } from "@ryerson/frontend.contentful";
import { companyToCompany } from "@components/Shared/model/Contentful";
import ContentfulPage, {
	IContentfulBaseFields,
	IContentfulPage,
} from "@components/Contentful/ContentfulPage";
import ProductPage, {
	ContentfulProductCategoryPageContent,
	ProductFields,
} from "@components/Contentful/Product";
import ProductSubCategoryPage, {
	ContentfulProductSubCategoryPage,
	SubProductFields,
} from "@components/Contentful/SubProduct";
import { ContentfulUtils } from "@components/Contentful/ContentfulUtils";

// The `page` fields `getStaticPaths` reads. `path` is the page's URL path from the site root;
// `website` lists the companies the page is built for, compared with `companyToCompany`'s value.
interface IContentfulPageStaticPath extends IContentfulBaseFields {
	path: string;
	website: string[];
}

interface SlugStaticPath {
	slug: string;
}

/**
 * Lists every path this page exports: the brand's Contentful `page` paths, then each
 * `productCategoryPage` slug and each `productSubCategoryPage` under it that no `page` already
 * claims. Reads the English (`en-US`) entries only.
 */
export const getStaticPaths = async () => {
	const company = companyToCompany(process.env.NEXT_PUBLIC_COMPANY || "");

	// Page
	const pages = (await new Crawl().get("page", {
		locales: ["en-US"],
		resolveRichText: false,
		depth: 1,
		include: ["path", "website"],
	})) as LanguageContent<IContentfulPageStaticPath[]>;
	const paths = pages.en
		.filter((page) => page.website.includes(company))
		.map((page: IContentfulPageStaticPath) => {
			return {
				params: {
					path: page.path.split("/").filter((segment) => segment !== ""),
				},
			};
		});

	// Product (due to conflicts with existing marketing pages)
	const productCategoryPages = (await new Crawl().get("productCategoryPage", {
		locales: ["en-US"],
		resolveRichText: false,
		depth: 1,
		include: ["slug"],
	})) as LanguageContent<SlugStaticPath[]>;
	for (const path of productCategoryPages.en) {
		if (paths.find((p) => p.params.path.length === 1 && p.params.path[0] === path.slug)) {
			// A path alread exists, do not add.
		} else {
			paths.push({
				params: {
					path: [path.slug],
				},
			});
		}
	}

	// Sub Product (due to conflicts with existing marketing pages)
	for (const path of productCategoryPages.en) {
		const baseSlug = path.slug;
		const productSubCategoryPages = (await new Crawl().get("productSubCategoryPage", {
			locales: ["en-US"],
			resolveRichText: false,
			filters: {
				"fields.baseSlug": baseSlug,
			},
			depth: 1,
			include: ["slug"],
		})) as LanguageContent<SlugStaticPath[]>;
		for (const subPath of productSubCategoryPages.en) {
			if (
				paths.find(
					(p) =>
						p.params.path.length === 2 &&
						p.params.path[0] === baseSlug &&
						p.params.path[1] === subPath.slug
				)
			) {
				// A path alread exists, do not add.
			} else {
				paths.push({
					params: {
						path: [baseSlug, subPath.slug],
					},
				});
			}
		}
	}

	return {
		paths: paths,
		fallback: false,
	};
};

/**
 * Fetches the content for one exported path, trying in order a `page` with this `path`, a
 * `productCategoryPage` (one segment), then a `productSubCategoryPage` (two segments). At most one
 * of `contentfulPage`, `product` and `subProduct` is set; the others are `null`. All three are
 * `null` when a `page`'s stored `path` doesn't round-trip through the segments (see the exact-path
 * rule in the file header), unless a `productCategoryPage` (one segment) or
 * `productSubCategoryPage` (two segments) has the same segments, in which case that product's
 * prop is set instead. A set value is a `LanguageContent` with `en`, `es` and `fr` entries.
 */
export const getStaticProps = async ({ params }: any) => {
	const { props: headerProps } = await allContentfulHeaderCalls();

	// Page
	const path = "/" + params.path.join("/");
	const pageContent: LanguageContent<IContentfulPage> | null =
		await ContentfulUtils.getPageContent(path);

	// Product
	let productPageContent: LanguageContent<ContentfulProductCategoryPageContent> | null = null;
	if (!pageContent && params.path.length === 1) {
		productPageContent = (await new Crawl().get("productCategoryPage", {
			filters: {
				"fields.slug": params.path[0],
			},
			resolveRichText: true,
			depth: 4,
			include: [...ProductFields],
		})) as LanguageContent<ContentfulProductCategoryPageContent>;
		// `Crawl.get` returns a single match as the entry itself and no match as `[]`.
		if (Array.isArray(productPageContent.en) && productPageContent.en.length === 0) {
			productPageContent = null;
		}
	}

	// Sub Product
	let subProductPageContent: LanguageContent<ContentfulProductSubCategoryPage> | null = null;
	if (!pageContent && !productPageContent && params.path.length === 2) {
		subProductPageContent = (await new Crawl().get("productSubCategoryPage", {
			filters: {
				"fields.baseSlug": params.path[0],
				"fields.slug": params.path[1],
			},
			resolveRichText: true,
			depth: 4,
			include: [...SubProductFields],
		})) as LanguageContent<ContentfulProductSubCategoryPage>;
		if (Array.isArray(subProductPageContent.en) && subProductPageContent.en.length === 0) {
			subProductPageContent = null;
		}
	}

	return {
		props: {
			...headerProps,
			contentfulPage: pageContent,
			product: productPageContent,
			subProduct: subProductPageContent,
		},
	};
};

type PageProps = {
	contentfulPage?: LanguageContent<IContentfulPage>;
	product?: LanguageContent<ContentfulProductCategoryPageContent>;
	subProduct?: LanguageContent<ContentfulProductSubCategoryPage>;
} & HeaderPageProps;

/** Renders whichever of the three content kinds `getStaticProps` found, inside the page chrome. */
function Page(props: PageProps) {
	return (
		<HeaderLayout {...getHeaderData(props)}>
			{props.contentfulPage && <ContentfulPage contentfulPage={props.contentfulPage} />}
			{props.product && <ProductPage contentfulProductPage={props.product} />}
			{props.subProduct && (
				<ProductSubCategoryPage contentfulSubProductPage={props.subProduct} />
			)}
		</HeaderLayout>
	);
}

export default Page;
